// Deterministic replays of tool design for Lesson 4.4 ("Good Tools Are Written for a Reader
// Who Cannot See Your Code"). Every entry, token count, tool call and sentence below was
// recorded from real runs on 8 October 2026, and the MCP lines are the shapes the workshop's
// server produces. The graphics replay those recordings and never call a model.

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
export const formatTokens = (tokens: number) => tokens.toLocaleString('en-US');

// --- tooldesign-description: the entry is the documentation --------------------------------

export const ENTRIES = ['full', 'one-word'] as const;
export type Entry = (typeof ENTRIES)[number];
export const entryLabels: Record<Entry, string> = {
  full: 'list_tasks(project_id, include_done), fully described',
  'one-word': 'list_tasks(p), described as "tasks"',
};
export const toEntry = (value: string): Entry => ENTRIES.find((entry) => entry === value) ?? 'full';
/** Each entry exactly as the request carried it. */
export const entryJson: Record<Entry, string> = {
  full: `{
  "name": "list_tasks",
  "description": "List the tasks of one project: id, title, done, assignee. Open tasks only, unless include_done is true. Use this to answer what is open, blocked, or assigned to whom.",
  "parameters": {
    "type": "object",
    "properties": {
      "project_id": {"type": "integer"},
      "include_done": {"type": "boolean"}
    },
    "required": ["project_id"]
  }
}`,
  'one-word': `{
  "name": "list_tasks",
  "description": "tasks",
  "parameters": {
    "type": "object",
    "properties": {"p": {"type": "integer"}},
    "required": ["p"]
  }
}`,
};
export const SUPPORT_QUESTION = 'What is open in Support?';
export const SUPPORT_ANSWER =
  'In Support, there is one open task: "Answer the refund tickets" (assigned to Jo).';
export interface DescriptionRun {
  promptTokens: number;
  /** The list_tasks request the model wrote after list_projects. */
  firstCall: string;
  answer: string;
}
export const descriptionRuns: Record<Entry, DescriptionRun> = {
  full: { promptTokens: 565, firstCall: 'list_tasks({"project_id": 2})', answer: SUPPORT_ANSWER },
  'one-word': { promptTokens: 417, firstCall: 'list_tasks({"p": 2})', answer: SUPPORT_ANSWER },
};
export interface DescriptionState {
  entry: Entry;
  asked: boolean;
}
export const initialDescription = (): DescriptionState => ({ entry: 'full', asked: false });
export type DescriptionAction =
  { type: 'entry'; value: Entry } | { type: 'ask' } | { type: 'reset' };
export function descriptionTransition(
  state: DescriptionState,
  action: DescriptionAction,
): DescriptionState {
  switch (action.type) {
    case 'reset':
      return initialDescription();
    case 'entry':
      return { entry: action.value, asked: false };
    case 'ask':
      return { ...state, asked: true };
  }
}
export const DESCRIPTION_START_MESSAGE = 'Choose an entry, then press Ask what is open in Support.';
export const DESCRIPTION_FULL_MESSAGE =
  'The full entry cost 565 tokens and told the model what list_tasks returns and needs.';
export const DESCRIPTION_ONE_WORD_MESSAGE =
  'The one-word entry worked here for 417 tokens and said nothing about when to call or what returns.';
export const DESCRIPTION_REFINEMENT =
  'Refinement: write for a reader who cannot see the code, and measure on harder questions.';
export function descriptionSummary(state: DescriptionState): Summary {
  if (!state.asked) return { tone: 'neutral', message: DESCRIPTION_START_MESSAGE };
  return state.entry === 'full'
    ? { tone: 'success', message: DESCRIPTION_FULL_MESSAGE }
    : { tone: 'warning', message: DESCRIPTION_ONE_WORD_MESSAGE };
}

// --- tooldesign-consolidate: fewer, clearer tools -------------------------------------------

export const CONSOLIDATE_SETS = ['four', 'eight'] as const;
export type ConsolidateSet = (typeof CONSOLIDATE_SETS)[number];
export const consolidateSetLabels: Record<ConsolidateSet, string> = {
  four: 'Four tools with filters',
  eight: 'Eight look-alike tools',
};
export const toConsolidateSet = (value: string): ConsolidateSet =>
  CONSOLIDATE_SETS.find((set) => set === value) ?? 'four';
export const toolNames: Record<ConsolidateSet, readonly string[]> = {
  four: ['list_projects', 'list_tasks(project_id, include_done)', 'get_task', 'list_members'],
  eight: [
    'list_projects',
    'get_task',
    'list_members',
    'list_open_tasks',
    'list_done_tasks',
    'list_tasks_by_assignee',
    'find_tasks_by_title',
    'list_unassigned_tasks',
  ],
};
export const ASK_SENTENCE =
  'If a request is ambiguous, ask which one is meant instead of guessing.';
export const RELEASE_NOTE_QUESTION = 'Who owns the release note task?';
export const ASKED_ANSWER =
  'I found two release note tasks. "Write the release note" (task 9) is owned by Ravi, while "Write the release note for Apollo" (task 15) is unassigned. Which one are you asking about?';
export const GUESSED_ANSWER =
  'Ravi owns the "Write the release note" task in the Apollo project. There\'s also an unassigned task "Write the release note for Apollo" in the same project.';
export const NOT_RECORDED = 'not measured for this question';
export interface ConsolidateRun {
  /** Prompt tokens of the first call, before any result came back. */
  promptTokens: number;
  toolsSent: number;
  did: string;
  answer: string | null;
}
export function runConsolidate(set: ConsolidateSet, askOn: boolean): ConsolidateRun {
  if (set === 'eight') return { promptTokens: 824, toolsSent: 8, did: NOT_RECORDED, answer: null };
  return askOn
    ? {
        promptTokens: 567,
        toolsSent: 4,
        did: 'listed both tasks and asked which one',
        answer: ASKED_ANSWER,
      }
    : {
        promptTokens: 567,
        toolsSent: 4,
        did: 'answered Ravi without asking',
        answer: GUESSED_ANSWER,
      };
}
export interface ConsolidateState {
  set: ConsolidateSet;
  askOn: boolean;
  run: ConsolidateRun | null;
}
export const initialConsolidate = (): ConsolidateState => ({
  set: 'four',
  askOn: true,
  run: null,
});
export type ConsolidateAction =
  | { type: 'set'; value: ConsolidateSet }
  | { type: 'toggle-ask' }
  | { type: 'ask' }
  | { type: 'reset' };
export function consolidateTransition(
  state: ConsolidateState,
  action: ConsolidateAction,
): ConsolidateState {
  switch (action.type) {
    case 'reset':
      return initialConsolidate();
    case 'set':
      return { ...state, set: action.value, run: null };
    case 'toggle-ask':
      return { ...state, askOn: !state.askOn, run: null };
    case 'ask':
      return { ...state, run: runConsolidate(state.set, state.askOn) };
  }
}
export const CONSOLIDATE_START_MESSAGE =
  'Choose a tool set, then press Ask who owns the release note task.';
export const CONSOLIDATE_ASKED_MESSAGE =
  'Two tasks matched, so the model listed both and asked which one Maya meant.';
export const CONSOLIDATE_GUESSED_MESSAGE =
  'Two tasks matched and the model picked Ravi without asking.';
export const CONSOLIDATE_EIGHT_MESSAGE =
  'Eight similar names cost 257 more tokens on every call, before any answer.';
export const CONSOLIDATE_REFINEMENT =
  'Refinement: fewer, clearer tools and a sentence that tells the model to ask.';
export function consolidateSummary(state: ConsolidateState): Summary {
  if (!state.run) return { tone: 'neutral', message: CONSOLIDATE_START_MESSAGE };
  if (state.set === 'eight') return { tone: 'warning', message: CONSOLIDATE_EIGHT_MESSAGE };
  return state.askOn
    ? { tone: 'success', message: CONSOLIDATE_ASKED_MESSAGE }
    : { tone: 'warning', message: CONSOLIDATE_GUESSED_MESSAGE };
}

// --- tooldesign-results: return only what the next step needs ------------------------------

export const RESULT_SHAPES = ['rows', 'pages', 'count'] as const;
export type ResultShape = (typeof RESULT_SHAPES)[number];
export const resultShapeLabels: Record<ResultShape, string> = {
  rows: 'All 66 rows at once',
  pages: 'Pages of 20',
  count: 'A count from code',
};
export const toResultShape = (value: string): ResultShape =>
  RESULT_SHAPES.find((shape) => shape === value) ?? 'rows';
export const COUNT_QUESTION = 'How many Apollo tasks are done, including completed ones?';
export const RIGHT_ANSWER = 22;
export const RIGHT_ANSWER_LINE = `Right answer: ${RIGHT_ANSWER}.`;
export interface ResultCall {
  /** What the model asked for, or "stops". */
  call: string;
  /** What our code sent back, abridged. */
  result: string;
  promptTokens: number;
}
const PROJECTS = 'Apollo (1), Support (2)';
const STOPS = 'stops';
const ANSWER = 'shows the answer to Maya';
const page = (number: number) =>
  `list_tasks({"project_id": 1, "include_done": true, "page": ${number}})`;
/** The recorded calls for each result shape, one entry per model call. */
export const resultTraces: Record<ResultShape, ResultCall[]> = {
  rows: [
    { call: 'list_projects({})', result: PROJECTS, promptTokens: 570 },
    {
      call: 'list_tasks({"project_id": 1, "include_done": true})',
      result: '66 rows',
      promptTokens: 635,
    },
    { call: STOPS, result: ANSWER, promptTokens: 2809 },
  ],
  pages: [
    { call: 'list_projects({})', result: PROJECTS, promptTokens: 591 },
    {
      call: 'list_tasks({"project_id": 1, "include_done": true})',
      result: '20 rows, more: true',
      promptTokens: 656,
    },
    { call: page(2), result: '20 rows, more: true', promptTokens: 1313 },
    { call: page(3), result: '20 rows, more: true', promptTokens: 2012 },
    { call: page(4), result: '8 rows, more: false', promptTokens: 2711 },
    { call: STOPS, result: ANSWER, promptTokens: 3026 },
  ],
  count: [
    { call: 'list_projects({})', result: PROJECTS, promptTokens: 648 },
    {
      call: 'count_tasks({"project_id": 1})',
      result: '{"open": 46, "done": 22}',
      promptTokens: 712,
    },
    { call: STOPS, result: ANSWER, promptTokens: 750 },
  ],
};
export const resultAnswers: Record<ResultShape, string> = {
  rows: 'There are 20 done tasks in the Apollo project.',
  pages: 'There are 18 done tasks in the Apollo project (out of 68 total tasks).',
  count: 'The Apollo project has 22 completed tasks.',
};
export const callsMade = (shape: ResultShape) => resultTraces[shape].length;
export const lastCallTokens = (shape: ResultShape) => resultTraces[shape].at(-1)?.promptTokens ?? 0;
export interface ResultsState {
  shape: ResultShape;
  asked: boolean;
}
export const initialResults = (): ResultsState => ({ shape: 'rows', asked: false });
export type ResultsAction =
  { type: 'shape'; value: ResultShape } | { type: 'ask' } | { type: 'reset' };
export function resultsTransition(state: ResultsState, action: ResultsAction): ResultsState {
  switch (action.type) {
    case 'reset':
      return initialResults();
    case 'shape':
      return { shape: action.value, asked: false };
    case 'ask':
      return { ...state, asked: true };
  }
}
export const RESULTS_START_MESSAGE = 'Choose a result shape, then press Ask how many are done.';
export const RESULTS_ROWS_MESSAGE =
  'The model read 66 rows for 2,809 tokens and counted 20, which is wrong.';
export const RESULTS_PAGES_MESSAGE =
  'Four pages took 6 calls and 3,026 tokens, and the model still counted wrong.';
export const RESULTS_COUNT_MESSAGE = 'Code counted 22 for 750 tokens, and the model repeated it.';
export const RESULTS_REFINEMENT =
  'Refinement: aggregate in code and return what the next step needs.';
export function resultsSummary(state: ResultsState): Summary {
  if (!state.asked) return { tone: 'neutral', message: RESULTS_START_MESSAGE };
  switch (state.shape) {
    case 'rows':
      return { tone: 'warning', message: RESULTS_ROWS_MESSAGE };
    case 'pages':
      return { tone: 'warning', message: RESULTS_PAGES_MESSAGE };
    case 'count':
      return { tone: 'success', message: RESULTS_COUNT_MESSAGE };
  }
}

// --- tooldesign-mcp: the same tools over a protocol -----------------------------------------

export const MCP_REQUESTS = ['initialize', 'tools-list', 'tools-call'] as const;
export type McpRequest = (typeof MCP_REQUESTS)[number];
export const mcpRequestLabels: Record<McpRequest, string> = {
  initialize: 'initialize',
  'tools-list': 'tools/list',
  'tools-call': 'tools/call list_tasks for project 1',
};
export const toMcpRequest = (value: string): McpRequest =>
  MCP_REQUESTS.find((request) => request === value) ?? 'initialize';
export const MCP_ANSWERED_BY = 'the server';
export interface McpExchange {
  method: string;
  /** The JSON-RPC request line, pretty-printed. */
  request: string;
  /** The server's response line, pretty-printed and abridged. */
  response: string;
  learns: string;
}
export const mcpExchanges: Record<McpRequest, McpExchange> = {
  initialize: {
    method: 'initialize',
    request: `{
  "jsonrpc": "2.0",
  "id": 1,
  "method": "initialize",
  "params": {
    "protocolVersion": "2025-06-18",
    "capabilities": {},
    "clientInfo": {"name": "collabboard-client", "version": "1"}
  }
}`,
    response: `{
  "jsonrpc": "2.0",
  "id": 1,
  "result": {
    "protocolVersion": "2025-06-18",
    "capabilities": {"tools": {}},
    "serverInfo": {"name": "collabboard-tools", "version": "1"}
  }
}`,
    learns: 'the protocol version, and that the server offers tools',
  },
  'tools-list': {
    method: 'tools/list',
    request: '{"jsonrpc": "2.0", "id": 2, "method": "tools/list"}',
    response: `{
  "jsonrpc": "2.0",
  "id": 2,
  "result": {
    "tools": [
      {
        "name": "list_projects",
        "description": "List the projects the signed-in person belongs to…",
        "inputSchema": {"type": "object", "properties": {}}
      },
      {
        "name": "list_tasks",
        "description": "List the tasks of one project…",
        "inputSchema": {
          "type": "object",
          "properties": {"project_id": {"type": "integer"}, "include_done": {"type": "boolean"}},
          "required": ["project_id"]
        }
      },
      … two more tools, 4 in all …
    ]
  }
}`,
    learns: 'four tool names, descriptions and schemas, as text',
  },
  'tools-call': {
    method: 'tools/call',
    request: `{
  "jsonrpc": "2.0",
  "id": 3,
  "method": "tools/call",
  "params": {"name": "list_tasks", "arguments": {"project_id": 1}}
}`,
    response: `{
  "jsonrpc": "2.0",
  "id": 3,
  "result": {
    "content": [
      {
        "type": "text",
        "text": "[{\\"id\\": 9, \\"title\\": \\"Write the release note\\", \\"done\\": false, \\"assignee\\": \\"Ravi\\"}, …]"
      }
    ],
    "isError": false
  }
}`,
    learns: 'the tasks of project 1, as text',
  },
};
export interface McpState {
  request: McpRequest;
  sent: boolean;
}
export const initialMcp = (): McpState => ({ request: 'initialize', sent: false });
export type McpAction =
  { type: 'request'; value: McpRequest } | { type: 'send' } | { type: 'reset' };
export function mcpTransition(state: McpState, action: McpAction): McpState {
  switch (action.type) {
    case 'reset':
      return initialMcp();
    case 'request':
      return { request: action.value, sent: false };
    case 'send':
      return { ...state, sent: true };
  }
}
export const MCP_START_MESSAGE = 'Choose a request, then press Send.';
export const MCP_INITIALIZE_MESSAGE =
  'The server agreed on the protocol version and said it offers tools.';
export const MCP_LIST_MESSAGE =
  "A client that trusts a server's descriptions blindly is reading text it did not write.";
export const MCP_CALL_MESSAGE =
  'The server ran list_tasks as the named person and returned the rows as text.';
export const MCP_REFINEMENT =
  'Refinement: the same tools over a protocol, with descriptions treated as untrusted text.';
export function mcpSummary(state: McpState): Summary {
  if (!state.sent) return { tone: 'neutral', message: MCP_START_MESSAGE };
  switch (state.request) {
    case 'initialize':
      return { tone: 'neutral', message: MCP_INITIALIZE_MESSAGE };
    case 'tools-list':
      return { tone: 'warning', message: MCP_LIST_MESSAGE };
    case 'tools-call':
      return { tone: 'success', message: MCP_CALL_MESSAGE };
  }
}
